/**
 * Keyboard navigation for the saved connections list.
 *
 * Supports ArrowDown/ArrowUp between cards, Enter on a focused card to
 * connect/disconnect, Escape to return to the search input.
 */

import { useState, useCallback, type RefObject } from 'react';
import type { Connection } from '../../../types/electron';

type UseConnectionListKeyboardParams = {
  visibleConnections: Connection[];
  activeConnectionIds: Set<string>;
  searchInputRef: RefObject<HTMLInputElement | null>;
  listRef: RefObject<HTMLDivElement | null>;
  onConnect: (connection: Connection) => void;
  onDisconnect: (connection: Connection) => void;
};

export function useConnectionListKeyboard({
  visibleConnections,
  activeConnectionIds,
  searchInputRef,
  listRef,
  onConnect,
  onDisconnect,
}: UseConnectionListKeyboardParams) {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const focusCard = useCallback((index: number) => {
    if (index < 0 || index >= visibleConnections.length) return;
    setFocusedIndex(index);
    const cards = listRef.current?.querySelectorAll<HTMLElement>('[data-connection-id]');
    const card = cards?.[index];
    if (card) {
      card.focus({ preventScroll: true });
      card.scrollIntoView({ block: 'nearest' });
    }
  }, [visibleConnections.length, listRef]);

  const focusSearch = useCallback(() => {
    setFocusedIndex(-1);
    searchInputRef.current?.focus();
  }, [searchInputRef]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusCard(0);
    } else if (e.key === 'Enter' && visibleConnections.length > 0) {
      e.preventDefault();
      const conn = visibleConnections[0];
      if (!activeConnectionIds.has(conn.id)) onConnect(conn);
    }
  }, [focusCard, visibleConnections, activeConnectionIds, onConnect]);

  const handleCardKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    const conn = visibleConnections[index];
    if (!conn) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (index < visibleConnections.length - 1) focusCard(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (index > 0) focusCard(index - 1);
        else focusSearch();
        break;
      case 'Enter':
        // Only the card itself: Enter on a control inside it (Edit, Connect, a
        // quick action such as Tables) bubbles here too and must activate that
        // control — never connect, or disconnect, the card instead.
        if (e.target !== e.currentTarget) break;
        e.preventDefault();
        if (activeConnectionIds.has(conn.id)) onDisconnect(conn);
        else onConnect(conn);
        break;
      case 'Escape':
        e.preventDefault();
        focusSearch();
        break;
    }
  }, [focusCard, focusSearch, visibleConnections, activeConnectionIds, onConnect, onDisconnect]);

  return {
    focusedIndex,
    setFocusedIndex,
    handleSearchKeyDown,
    handleCardKeyDown,
  };
}
